import React from 'react'
import { Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * SyncCard (W2) — the "connecting your PMS" onboarding vignette for How-It-Works
 * step 1. Three neutral, PMS-agnostic rows tick to a ✓ "Done" state one after
 * another, a teal shimmer sweeps the progress bar, and the caption flips from
 * "Setting things up…" to "Ready to preview."
 *
 * Copy is VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §W2 — do not
 * edit strings here without updating that doc + the claims register. PMS-neutral:
 * "your PMS" only; the row labels carry the "learns from your history from day
 * one" value WITHOUT a day-count (§0 sync-depth ban).
 *
 * Animates ONCE when it scrolls into view (IntersectionObserver), matching the
 * marketing2 SystemLog pattern. prefers-reduced-motion / no IntersectionObserver
 * → the fully-completed card, static (a designed still, not a broken animation).
 */

interface SyncRow {
  label: string
  /** the per-row completion word ("Synced" / "Loaded" / "Ready") */
  doneWord: string
}

// Verbatim from copy doc §W2
const SYNC_ROWS: SyncRow[] = [
  { label: 'Rooms and rate plans', doneWord: 'Synced' },
  { label: 'Booking history', doneWord: 'Loaded' },
  { label: 'Your calendar', doneWord: 'Ready' },
]

const START_DELAY_MS = 400
const ROW_INTERVAL_MS = 780

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function SyncCard({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [reduced] = React.useState(prefersReducedMotion)
  const [started, setStarted] = React.useState(false)
  const [done, setDone] = React.useState(reduced ? SYNC_ROWS.length : 0)

  React.useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduced])

  React.useEffect(() => {
    if (reduced || !started) return
    const timers: ReturnType<typeof setTimeout>[] = []
    for (let i = 1; i <= SYNC_ROWS.length; i++) {
      timers.push(setTimeout(() => setDone(i), START_DELAY_MS + (i - 1) * ROW_INTERVAL_MS))
    }
    return () => timers.forEach(clearTimeout)
  }, [started, reduced])

  const complete = done >= SYNC_ROWS.length

  return (
    <div
      ref={ref}
      className={cn('mkt3-panel overflow-hidden', className)}
      role="img"
      aria-label="A setup card connecting your PMS — rooms and rate plans synced, booking history loaded, calendar ready."
    >
      {/* Card header */}
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mkt3-hairline)] px-5 py-3.5">
        <span className="mkt3-label">Connecting your PMS</span>
        <span
          aria-hidden="true"
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            complete ? 'mkt3-live-dot bg-teal-500 shadow-[0_0_8px_rgba(13,148,136,0.7)]' : 'bg-slate-300'
          )}
        />
      </div>

      {/* Rows */}
      <ul className="divide-y divide-[var(--mkt3-hairline)]">
        {SYNC_ROWS.map((row, i) => {
          const rowDone = done > i
          return (
            <li key={row.label} className="flex items-center justify-between gap-3 px-5 py-3.5">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors duration-500',
                    rowDone ? 'bg-teal-600 text-white' : 'border border-slate-300 text-transparent'
                  )}
                >
                  {rowDone ? (
                    <Check className={cn('h-3 w-3', !reduced && 'mkt3-pop')} strokeWidth={3} />
                  ) : (
                    <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                  )}
                </span>
                <span
                  className={cn(
                    'text-sm transition-colors duration-500',
                    rowDone ? 'text-[var(--mkt3-ink)]' : 'text-[var(--mkt3-muted)]'
                  )}
                >
                  {row.label}
                </span>
              </div>
              <span
                className={cn(
                  'font-mono text-[11px] font-semibold uppercase tracking-[0.14em] tabular-nums transition-colors duration-500',
                  rowDone ? 'text-teal-700' : 'text-slate-300'
                )}
              >
                {rowDone ? row.doneWord : '…'}
              </span>
            </li>
          )
        })}
      </ul>

      {/* Progress bar + caption */}
      <div className="border-t border-[var(--mkt3-hairline)] px-5 py-4">
        <div className="relative h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-teal-600 transition-[width] duration-700 ease-out"
            style={{ width: `${(done / SYNC_ROWS.length) * 100}%` }}
          />
          {!complete && !reduced && (
            <span aria-hidden="true" className="mkt3-shimmer absolute inset-0 rounded-full" />
          )}
        </div>
        <p className="mt-3 font-mono text-[11px] tracking-[0.04em] text-[var(--mkt3-muted)]">
          {complete ? (
            <span className="font-semibold text-teal-700">Ready to preview.</span>
          ) : (
            <>
              Setting things up
              <span className="mkt3-cursor ml-0.5 inline-block">…</span>
            </>
          )}
        </p>
      </div>
    </div>
  )
}
